import {
  isConsentLinkState,
  isRateLimited,
  linkStateFromError,
  type ConsentLinkResult,
  type ConsentLinkState,
} from '@/lib/consent-public';
import {
  CONSENT_SUBMIT_FAILED_MESSAGE,
  CONSENT_SUBMIT_RATE_LIMITED_MESSAGE,
  CONSENT_WITHDRAW_CHANGED_MESSAGE,
  CONSENT_WITHDRAW_WRITE_TO_US_MESSAGE,
} from './consent-link-copy';

// HW-1024: what the public consent page does on open and on "I consent",
// kept out of the component so it can be tested in vitest's node
// environment. The product rule it carries: opening the link only reads —
// mail scanners open links on their own — and only the button confirms.

/**
 * What the page shows besides the link states themselves:
 * FAILED — the link could not be read (network, 5xx); retrying may help.
 * RATE_LIMITED — the per-IP limit refused it; retrying now will not help.
 * UNSUPPORTED — the server answered a state this page does not know (a newer
 *   backend); retrying returns the same answer, so none is offered.
 * WITHDRAWN — the candidate withdrew their consent from this page (HW-1057).
 */
export type ConsentPagePhase =
  | 'loading'
  | 'FAILED'
  | 'RATE_LIMITED'
  | 'UNSUPPORTED'
  | 'WITHDRAWN'
  | ConsentLinkState;

export interface ConsentPageApi {
  getConsentLink: (token: string) => Promise<ConsentLinkResult>;
  confirmConsent: (token: string) => Promise<ConsentLinkResult>;
  withdrawConsent: (token: string) => Promise<ConsentLinkResult>;
}

export interface ConsentPageUpdate {
  phase?: ConsentPagePhase;
  contactEmail?: string;
  submitError?: string;
}

const fromResult = (result: ConsentLinkResult): ConsentPageUpdate => ({
  phase: isConsentLinkState(result.state) ? result.state : 'UNSUPPORTED',
  contactEmail: result.contactEmail,
});

/** Opening the page: reads the link's state, never confirms. */
export async function loadConsentPage(
  api: ConsentPageApi,
  token: string
): Promise<ConsentPageUpdate> {
  try {
    return fromResult(await api.getConsentLink(token));
  } catch (err) {
    return { phase: isRateLimited(err) ? 'RATE_LIMITED' : 'FAILED' };
  }
}

/**
 * The "I consent" button. A refused link moves the page to that link's
 * state; any other failure keeps the form and says so beside the button.
 */
export async function submitConsent(
  api: ConsentPageApi,
  token: string
): Promise<ConsentPageUpdate> {
  try {
    return fromResult(await api.confirmConsent(token));
  } catch (err) {
    const state = linkStateFromError(err);
    if (state) return { phase: state };
    return {
      submitError: isRateLimited(err)
        ? CONSENT_SUBMIT_RATE_LIMITED_MESSAGE
        : CONSENT_SUBMIT_FAILED_MESSAGE,
    };
  }
}

/**
 * HW-1057: the "Withdraw consent" button on a confirmed link. Success — also a
 * repeat — shows that the consent is withdrawn; a link that cannot withdraw
 * moves the page to that link's state; a refusal to withdraw here, a consent
 * that changed meanwhile and any other failure keep the page and say so
 * beside the button.
 */
export async function submitWithdraw(
  api: ConsentPageApi,
  token: string
): Promise<ConsentPageUpdate> {
  try {
    const result = await api.withdrawConsent(token);
    return { phase: 'WITHDRAWN', contactEmail: result.contactEmail };
  } catch (err) {
    const state = linkStateFromError(err);
    if (state && state !== 'PENDING') return { phase: state };
    return {
      submitError: isRateLimited(err)
        ? CONSENT_SUBMIT_RATE_LIMITED_MESSAGE
        : codeOf(err) === 'WRITE_TO_US'
          ? CONSENT_WITHDRAW_WRITE_TO_US_MESSAGE
          : codeOf(err) === 'CONSENT_CHANGED'
            ? CONSENT_WITHDRAW_CHANGED_MESSAGE
            : CONSENT_SUBMIT_FAILED_MESSAGE,
    };
  }
}

function codeOf(err: unknown): unknown {
  return (err as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
}
